import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import Iconify from "src/components/iconify";
import useService from "src/hooks/useService";
import ChatMessageList from "src/sections/@dashboard/chat/message/ChatMessageList";
import ChatMessageInput from "src/sections/@dashboard/chat/message/ChatMessageInput";
import { IChatConversation, IChatSendMessage } from "src/@types/chat";
import FunctionCallLogList from "./FunctionCallLogList";

type Props = {
  open: boolean;
  onClose: () => void;
};

const TEST_CONVERSATION: IChatConversation = {
  clientId: { _id: "test", phone: "", name: "Chat de teste" },
};

export default function AssistantTestChatModal({ open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [showLogs, setShowLogs] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const {
    data: dataHistory,
    refetch: triggerHistory,
    loading: loadingHistory,
  } = useService("/assistant-test-chat", { manual: true });

  const { post: triggerSend } = useService("/assistant-test-chat/messages", {
    manual: true,
  });

  const { remove: triggerClear, removeLoading: clearLoading } = useService(
    "/assistant-test-chat/messages",
    { manual: true }
  );

  useEffect(() => {
    if (open) {
      triggerHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const history = dataHistory?.history?.historicChat ?? [];

  const handleSend = async (data: IChatSendMessage) => {
    const result = await triggerSend({ message: data.message });
    if (result?.error) {
      const status = (result.error as any)?.response?.status;
      enqueueSnackbar(
        status === 403
          ? "Sem plano ativo. Renove seu plano para testar o assistente."
          : "Erro ao enviar mensagem de teste.",
        { variant: "error" }
      );
      return;
    }
    await triggerHistory();
  };

  const handleConfirmClear = async () => {
    const result = await triggerClear({});
    setConfirmClearOpen(false);
    if (result?.error) {
      enqueueSnackbar("Erro ao limpar a conversa de teste.", { variant: "error" });
      return;
    }
    await triggerHistory();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Testar assistente</Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showLogs}
                    onChange={(event) => setShowLogs(event.target.checked)}
                  />
                }
                label="Ver logs"
              />
              <Button
                color="error"
                variant="outlined"
                size="small"
                onClick={() => setConfirmClearOpen(true)}
              >
                Limpar conversa
              </Button>
              <IconButton onClick={onClose}>
                <Iconify icon="eva:close-fill" />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0, height: "70vh" }}>
          {loadingHistory && !dataHistory ? (
            <Stack sx={{ height: 1 }} alignItems="center" justifyContent="center">
              <CircularProgress size={26} />
            </Stack>
          ) : (
            <Stack direction="row" sx={{ height: 1 }}>
              <Stack
                flexGrow={1}
                sx={{
                  minWidth: 0,
                  borderRight: showLogs ? (theme) => `1px solid ${theme.palette.divider}` : "none",
                }}
              >
                <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                  <ChatMessageList conversation={TEST_CONVERSATION} history={history} />
                </Box>
                <ChatMessageInput
                  clientId="test"
                  conversationId="test"
                  onSend={handleSend}
                  hideAiSuggestion
                />
              </Stack>

              {showLogs && (
                <Box sx={{ width: 360, flexShrink: 0, overflow: "hidden" }}>
                  <FunctionCallLogList history={history} />
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
        <DialogTitle>Atenção</DialogTitle>
        <DialogContent sx={{ textAlign: "left" }}>
          <Typography variant="body2" color="text.secondary">
            Isso vai apagar todo o histórico da conversa de teste. A memória
            temporária salva pelo assistente também será perdida.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmClear}
            disabled={clearLoading}
          >
            {clearLoading ? "Limpando..." : "Limpar conversa"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
